import { Epic } from 'redux-observable';
import { catchError, debounceTime, map, switchMap, withLatestFrom } from 'rxjs/operators';
import { ofType } from '@alycecom/utils';
import { handleError, handlers, IResponse } from '@alycecom/services';

import { IProduct } from '../../types/Product.types';
import { TCountry } from '../../../../store/countries/countries.types';
import { getCountryById } from '../../../../store/countries/countries.selectors';

import { fetchShippingAddress, validateShipping } from './shipping.actions';
import { IAddressValidationResponse, IShippingAddress } from './shipping.types';

const prepareValidationData = ({
  product,
  address: { state, ...address },
  country,
}: {
  product: IProduct | null;
  address: IShippingAddress;
  country?: TCountry;
}) => ({
  product_id: product ? product.id : 0,
  address: {
    ...address,
    state,
    state_code: country?.states?.find(item => item.name === state)?.abbreviation,
  },
});

export const validateShippingEpic: Epic = (action$, state$, { apiService }) =>
  action$.pipe(
    ofType(validateShipping.pending),
    debounceTime(300),
    withLatestFrom(state$),
    switchMap(
      ([
        {
          payload: { hashId, product, address },
        },
        state,
      ]) => {
        const country = getCountryById(address.country)(state);

        return apiService
          .post(`/api/recipient/${hashId}/validate/address`, {
            body: prepareValidationData({ product, address, country }),
          })
          .pipe(
            map(({ address: verifiedAddress, isAddressVerified, isAddressChanged }: IAddressValidationResponse) =>
              validateShipping.fulfilled({
                address,
                verifiedAddress: verifiedAddress ? { ...verifiedAddress, countryId: address.country } : null,
                isAddressVerified,
                isAddressChanged,
              }),
            ),
            catchError(handleError(handlers.handleAnyError(validateShipping.rejected))),
          );
      },
    ),
  );

export const fetchShippingAddressEpic: Epic = (action$, state$, { apiService }) =>
  action$.pipe(
    ofType(fetchShippingAddress.pending),
    switchMap(({ payload: { hashId, countryId } }) =>
      apiService.get(`/api/recipient/${hashId}/address?countryId=${countryId}`, null).pipe(
        map(({ data }: IResponse<IShippingAddress>) => fetchShippingAddress.fulfilled(data)),
        catchError(handleError(handlers.handleAnyError(fetchShippingAddress.rejected()))),
      ),
    ),
  );

export const shippingEpics = [validateShippingEpic, fetchShippingAddressEpic];
